import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { logger } from '../lib/logger';

export interface PageHistoryEntry {
  path: string;
  title: string;
  icon: string;
  timestamp: number;
}

const MAX_HISTORY_ENTRIES = 10;

const EXCLUDED_PATHS = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/force-change-password'
];

const getPageInfo = (pathname: string): { title: string; icon: string } => {
  const routes: Record<string, { title: string; icon: string }> = {
    '/': { title: 'Accueil', icon: 'ri-home-4-line' },
    '/dashboard': { title: 'Tableau de bord', icon: 'ri-dashboard-line' },
    '/mon-espace': { title: 'Mon Espace', icon: 'ri-user-line' },
    '/dossiers': { title: 'Gestion des Dossiers', icon: 'ri-folder-line' },
    '/nouveau-dossier': { title: 'Nouveau Dossier', icon: 'ri-file-add-line' },
    '/parties': { title: 'Gestion des Parties', icon: 'ri-group-line' },
    '/demandes': { title: 'Demandes d\'Information', icon: 'ri-mail-send-line' },
    '/suivi-reponses': { title: 'Suivi des Réponses', icon: 'ri-time-line' },
    '/notifications-alertes': { title: 'Notifications & Alertes', icon: 'ri-notification-3-line' },
    '/gestion-documentaire': { title: 'Gestion Documentaire', icon: 'ri-file-text-line' },
    '/rapports-dissemination': { title: 'Rapports de Dissémination', icon: 'ri-file-shield-line' },
    '/gestion-organisations': { title: 'Gestion des Organisations', icon: 'ri-building-line' },
    '/gestion-utilisateurs': { title: 'Gestion des Utilisateurs', icon: 'ri-user-settings-line' },
    '/administration': { title: 'Administration', icon: 'ri-settings-3-line' },
    '/parametres': { title: 'Paramètres', icon: 'ri-equalizer-line' },
    '/profil': { title: 'Mon Profil', icon: 'ri-user-3-line' },
    '/superviseur': { title: 'Espace Superviseur', icon: 'ri-shield-user-line' },
    '/espace-enqueteur': { title: 'Espace Enquêteur', icon: 'ri-search-eye-line' },
    '/espace-collaborateur': { title: 'Espace Collaborateur', icon: 'ri-team-line' },
    '/espace-lecteur': { title: 'Espace Lecteur', icon: 'ri-eye-line' },
    '/statistiques-delais': { title: 'Statistiques des Délais', icon: 'ri-bar-chart-line' },
    '/conformite-organisations': { title: 'Conformité des Organisations', icon: 'ri-shield-check-line' },
    '/suivi-delais-72h': { title: 'Suivi des Délais 72h', icon: 'ri-alarm-warning-line' },
    '/stats-type-organisation': { title: 'Stats par Type d\'Organisation', icon: 'ri-pie-chart-line' },
    '/reporting': { title: 'Reporting', icon: 'ri-file-chart-line' },
    '/recherche-avancee': { title: 'Recherche Avancée', icon: 'ri-search-2-line' }
  };

  // Check for detail pages
  if (pathname.startsWith('/dossier/')) {
    return { title: 'Détail du Dossier', icon: 'ri-folder-open-line' };
  }
  if (pathname.startsWith('/demande/')) {
    return { title: 'Détail de la Demande', icon: 'ri-mail-open-line' };
  }
  if (pathname.startsWith('/rapports-dissemination/nouveau')) {
    return { title: 'Nouveau Rapport', icon: 'ri-file-add-line' };
  }

  return routes[pathname] || { title: pathname, icon: 'ri-file-line' };
};

export const usePageHistory = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [history, setHistory] = useState<PageHistoryEntry[]>([]);

  const storageKey = user ? `page_history_${user.id}` : 'page_history_guest';

  // Load history from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        setHistory(parsed);
      }
    } catch (error) {
      logger.error('Error loading page history:', error);
    }
  }, [storageKey]);

  // Track page visits
  useEffect(() => {
    const pathname = location.pathname;

    // Exclude certain paths
    if (EXCLUDED_PATHS.some(excluded => pathname.startsWith(excluded))) {
      return;
    }

    const pageInfo = getPageInfo(pathname);
    const newEntry: PageHistoryEntry = {
      path: pathname,
      title: pageInfo.title,
      icon: pageInfo.icon,
      timestamp: Date.now()
    };

    setHistory(prevHistory => {
      // Remove duplicate entries (same path)
      const filtered = prevHistory.filter(entry => entry.path !== pathname);
      
      // Add new entry at the beginning
      const updated = [newEntry, ...filtered].slice(0, MAX_HISTORY_ENTRIES);

      // Save to localStorage
      try {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      } catch (error) {
        logger.error('Error saving page history:', error);
      }

      return updated;
    });
  }, [location.pathname, storageKey]);

  const clearHistory = () => {
    setHistory([]);
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      logger.error('Error clearing page history:', error);
    }
  };

  return {
    history,
    clearHistory
  };
};